import type { AccessProfile, NavKey, OrganizationRole, Workspace } from './types';

const ROLE_LABELS: Record<OrganizationRole, string> = {
  stakd_super_admin: 'STAKD Super Admin',
  partner_admin: 'Sponsor Admin',
  program_manager: 'Program Manager',
  facilitator: 'Facilitator',
  analyst_viewer: 'Analyst',
};

const SPONSORED_NAV: Record<OrganizationRole, NavKey[]> = {
  stakd_super_admin: ['home', 'programs', 'cohorts', 'participants', 'reports', 'resources', 'settings', 'help'],
  partner_admin: ['home', 'programs', 'cohorts', 'participants', 'reports', 'resources', 'settings', 'help'],
  program_manager: ['home', 'programs', 'cohorts', 'participants', 'reports', 'resources', 'help'],
  facilitator: ['home', 'cohorts', 'participants', 'resources', 'help'],
  analyst_viewer: ['home', 'reports', 'help'],
};

const NAV_PERMISSIONS: Partial<Record<NavKey, string[]>> = {
  programs: ['programs.read'],
  cohorts: ['cohorts.read'],
  participants: ['participants.read'],
  reports: ['reports.read'],
  'creator-partners': ['partners.read'],
  'revenue-payouts': ['commissions.read', 'clawbacks.read', 'payouts.read'],
  resources: ['programs.read'],
  settings: ['programs.settings.manage', 'staff.roles.read', 'audit.read', 'settings.read'],
};

const WRITE_PERMISSIONS = new Set([
  'partners.create', 'partners.status.manage', 'codes.issue', 'codes.revoke',
  'commissions.plans.manage', 'commissions.assign', 'commissions.adjust',
  'clawbacks.review', 'payouts.build', 'payouts.approve', 'payouts.mark_paid', 'payouts.export',
  'programs.manage', 'programs.settings.manage', 'cohorts.manage',
  'participants.manage', 'participants.attendance.manage', 'reports.create', 'reports.export',
  'staff.roles.manage', 'staff.assignments.manage', 'settings.manage',
]);

export function hasPermission(profile: AccessProfile, key: string, organizationId: string | null = null): boolean {
  return profile.permissions.some((grant) =>
    (grant.key === key || grant.key === '*')
    && (organizationId === null ? grant.organizationId === null :
      (grant.organizationId === null || grant.organizationId === organizationId)),
  );
}

function hasRbacProfile(profile: AccessProfile): boolean { return profile.permissions.length > 0; }

function navFromPermissions(profile: AccessProfile, organizationId: string | null, candidates: NavKey[]): NavKey[] {
  const result: NavKey[] = ['home'];
  for (const key of candidates) {
    if (key === 'home' || key === 'help') continue;
    const required = NAV_PERMISSIONS[key] ?? [];
    if (required.some((permission) => hasPermission(profile, permission, organizationId))) result.push(key);
  }
  result.push('help');
  return [...new Set(result)];
}

export const ADMIN_NAV: NavKey[] = [
  'home', 'programs', 'cohorts', 'participants', 'reports',
  'creator-partners', 'revenue-payouts', 'resources', 'settings', 'help',
];

export const CREATOR_NAV: NavKey[] = ['home', 'participants', 'reports', 'revenue-payouts', 'resources', 'settings', 'help'];

export function workspacesFor(profile: AccessProfile): Workspace[] {
  const workspaces: Workspace[] = profile.organizations.map((membership) => ({
    id: `org:${membership.organizationId}`,
    kind: 'sponsored',
    name: membership.organizationName,
    roleLabel: membership.roleLabel ?? ROLE_LABELS[membership.role],
    organizationId: membership.organizationId,
  }));

  if (profile.creator) {
    workspaces.push({
      id: `creator:${profile.creator.partnerId}`,
      kind: 'creator',
      name: profile.creator.displayName,
      roleLabel: profile.creator.tier === 'founding' ? 'Founder Partner' : 'Creator Partner',
      partnerId: profile.creator.partnerId,
    });
  }

  const hasGlobalRbac = profile.permissions.some((grant) => grant.organizationId === null);
  if (hasGlobalRbac || (!hasRbacProfile(profile) && profile.isStaff)) {
    workspaces.push({ id: 'admin:global', kind: 'admin', name: 'STAKD Administration', roleLabel: profile.isStaff ? 'STAKD Super Admin' : 'STAKD Staff' });
  }

  return workspaces;
}

export function navFor(workspace: Workspace, profile: AccessProfile): NavKey[] {
  if (workspace.kind === 'admin') {
    if (hasRbacProfile(profile)) return navFromPermissions(profile, null, ADMIN_NAV);
    return profile.isStaff ? ADMIN_NAV : [];
  }
  if (workspace.kind === 'creator') return profile.creator?.partnerId === workspace.partnerId ? CREATOR_NAV : [];
  const membership = profile.organizations.find((entry) => entry.organizationId === workspace.organizationId);
  if (!membership) return [];
  return hasRbacProfile(profile)
    ? navFromPermissions(profile, workspace.organizationId ?? null, SPONSORED_NAV.stakd_super_admin)
    : SPONSORED_NAV[membership.role];
}

export function canWrite(workspace: Workspace, profile: AccessProfile): boolean {
  if (workspace.kind === 'admin') {
    if (!hasRbacProfile(profile)) return profile.isStaff;
    return profile.permissions.some((grant) => grant.organizationId === null && WRITE_PERMISSIONS.has(grant.key))
      || hasPermission(profile, '*', null);
  }
  if (workspace.kind === 'creator') return profile.creator?.partnerId === workspace.partnerId;
  if (hasRbacProfile(profile)) return profile.permissions.some((grant) =>
    (grant.organizationId === null || grant.organizationId === workspace.organizationId)
      && WRITE_PERMISSIONS.has(grant.key),
  ) || hasPermission(profile, '*', workspace.organizationId ?? null);
  const role = profile.organizations.find((entry) => entry.organizationId === workspace.organizationId)?.role;
  return role !== undefined && role !== 'analyst_viewer';
}
